import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { withPage } from "../../components/Page";
import { PageHeader } from "../../components/PageHeader";
import { QuickLinkTile } from "../../components/QuickLinkTile";
import {
  DrawingScheduleTable,
  TsaFloorPlanRow,
  AdditionalRecordRow,
} from "../../components/DrawingScheduleTable";
import { DrawingCollectionCard } from "../../components/DrawingCollectionCard";
import { InfoCallout } from "../../components/InfoCallout";
import styles from "./Drawings.module.scss";
import { clientContent } from "../../client/content";

const TSA_ROWS: TsaFloorPlanRow[] = [
  {
    id: "tsa-plantroom",
    autocadVersion: "Plantroom Sample",
    source: "TSA Survey",
    titleLevel: "Plant Room Level",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/plantroom-sample.webp",
  },
  {
    id: "tsa-plantroom-1",
    autocadVersion: "Plantroom Mezzanine Sample",
    source: "TSA Survey",
    titleLevel: "Plant Room Mezzanine",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/plantroom-sample-1.webp",
  },
  {
    id: "tsa-roof-plant",
    autocadVersion: "Roof Plant Sample",
    source: "TSA Survey",
    titleLevel: "Roof Plant Level",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/roof-plant-sample.webp",
  },
  {
    id: "tsa-ductwork",
    autocadVersion: "Ductwork Layout Sample",
    source: "TSA Survey",
    titleLevel: "Level 01 - Ventilation",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/ductwork-sample.webp",
  },
  {
    id: "tsa-electrical",
    autocadVersion: "Electrical Distribution Sample",
    source: "TSA Survey",
    titleLevel: "Level 02 - Electrical",
    discipline: "Electrical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/electrical-sample.webp",
  },
  {
    id: "tsa-lighting",
    autocadVersion: "Lighting Layout Sample",
    source: "TSA Survey",
    titleLevel: "Level 03 - Lighting",
    discipline: "Electrical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/lighting-sample.webp",
  },
  {
    id: "tsa-heating",
    autocadVersion: "Heating Services Sample",
    source: "TSA Survey",
    titleLevel: "Level 04 - Heating",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/heating-sample.webp",
  },
  {
    id: "tsa-heating-2",
    autocadVersion: "Heating Zone B Sample",
    source: "TSA Survey",
    titleLevel: "Level 05 - Heating Zone B",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/heating-sample-2.webp",
  },
  {
    id: "tsa-pipework-2",
    autocadVersion: "Pipework Schematics Sample",
    source: "TSA Survey",
    titleLevel: "Level 06 - Pipework",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/pipework-sample-2.webp",
  },
  {
    id: "tsa-ufh",
    autocadVersion: "Underfloor Heating Sample",
    source: "TSA Survey",
    titleLevel: "Ground Floor - UFH",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/ufh-sample.webp",
  },
  {
    id: "tsa-water-services",
    autocadVersion: "Water Services Sample",
    source: "TSA Survey",
    titleLevel: "Basement - Water Services",
    discipline: "Plumbing",
    compiledBy: "TSA",
    date: "2026",
    autocadHref: "/documents/drawings/table/water-services-sample.webp",
  },
];

const ADDITIONAL_ROWS: AdditionalRecordRow[] = [
  {
    id: "add-1",
    filename: "6000-RDM-S-903 Gas Schematic.pdf",
    fileType: "PDF",
    description: "Mechanical Services Gas System Schematic",
    discipline: "Mechanical",
  },
  {
    id: "add-2",
    filename: "9703-601.pdf",
    fileType: "PDF",
    description: "LV Distribution System LV02–LV04",
    discipline: "Electrical",
  },
  {
    id: "add-3",
    filename: "9703-601_AB.dwg",
    fileType: "DWG",
    description: "LV Distribution System LV02–LV04",
    discipline: "Electrical",
  },
];

const Drawings: FC = () => {
  const navigate = useNavigate();

  return (
    <div className="page-container-right">
      <PageHeader
        eyebrow="DRAWINGS / MERIDIAN TOWER"
        title="Drawing schedule"
        subtitle="Floor plans, key schematics and linked drawing collections."
        secondaryAction={{
          label: "Export schedule",
          href: "/more-info",
        }}
        primaryAction={{
          label: "Search drawings",
          href: "/search",
          onClick: (e) => {
            e.preventDefault();
            navigate("/search");
          },
        }}
      />

      <section
        className={styles.keySchematicsSection}
        aria-labelledby="key-schematics-heading"
      >
        <div className={styles.sectionHeaderRow}>
          <div>
            <h2 id="key-schematics-heading" className={styles.sectionTitle}>
              Key schematics
            </h2>
            <p className={styles.sectionSubtitle}>
              Direct access to frequently needed drawing information
            </p>
          </div>
        </div>

        <div className={styles.quickLinksGrid}>
          <QuickLinkTile
            title="ChW schematic"
            subtitle="Mechanical services"
            icon="~"
            href="/documents/drawings/schematic/schematic-sample.webp"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              window.open(
                "/documents/drawings/schematic/schematic-sample.webp",
                "_blank",
              );
            }}
          />
          <QuickLinkTile
            title="ChW riser schematic"
            subtitle="Mechanical services"
            icon="↑"
            href="/documents/drawings/schematic/schematic-sample-1.webp"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              window.open(
                "/documents/drawings/schematic/schematic-sample-1.webp",
                "_blank",
              );
            }}
          />
          <QuickLinkTile
            title="LTHW schematic"
            subtitle="Mechanical services"
            icon="~"
            href="/documents/drawings/schematic/schematic-sample-2.webp"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              window.open(
                "/documents/drawings/schematic/schematic-sample-2.webp",
                "_blank",
              );
            }}
          />
          <QuickLinkTile
            title="LV schematics"
            subtitle="LV01–LV04"
            icon="⚡"
            href="/documents/drawings/schematic/schematic-sample-3.webp"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              window.open(
                "/documents/drawings/schematic/schematic-sample-3.webp",
                "_blank",
              );
            }}
          />
          <QuickLinkTile
            title="Fire & ventilation"
            subtitle="Fire alarm, dampers and ventilation"
            icon="○"
            href="/more-info"
          />
        </div>
      </section>

      <div className={styles.mainLayoutGrid}>
        <main className={styles.mainColumn}>
          <section
            className={styles.contentSection}
            aria-labelledby="tsa-floor-plans-heading"
          >
            <div className={styles.sectionHeaderRow}>
              <div>
                <h2
                  id="tsa-floor-plans-heading"
                  className={styles.sectionTitle}
                >
                  TSA floor plans
                </h2>
                <p className={styles.sectionSubtitle}>
                  General floor plans in AutoCAD and PDF format.
                </p>
              </div>
              <div className={styles.filterControlsRow}>
                <button
                  type="button"
                  className={styles.filterChip}
                  aria-haspopup="listbox"
                >
                  Floor: <span>All</span> ▾
                </button>
                <button
                  type="button"
                  className={styles.filterChip}
                  aria-haspopup="listbox"
                >
                  Format: <span>All</span> ▾
                </button>
              </div>
            </div>

            <DrawingScheduleTable
              variant="tsa"
              columns={[
                "CAD DRAWING FILE",
                "SOURCE",
                "TITLE / LEVEL",
                "DISCIPLINE",
                "COMPILED BY",
                "DATE",
              ]}
              tsaRows={TSA_ROWS}
              ariaLabel="TSA Floor Plans Schedule"
            />

            <InfoCallout variant="note">
              <strong>Floor-plan record:</strong> These will be YOUR drawings.
              We import what you provide and sort in a logical order.
            </InfoCallout>
          </section>

          <section
            className={styles.contentSection}
            aria-labelledby="additional-records-heading"
          >
            <div className={styles.sectionHeaderRow}>
              <div>
                <h2
                  id="additional-records-heading"
                  className={styles.sectionTitle}
                >
                  Additional drawing records
                </h2>
                <p className={styles.sectionSubtitle}>
                  Archive files are included here rather than hidden behind a
                  separate destination.
                </p>
              </div>
            </div>

            <DrawingScheduleTable
              variant="additional"
              columns={["FILENAME", "DESCRIPTION", "DISCIPLINE"]}
              additionalRows={ADDITIONAL_ROWS}
              ariaLabel="Additional Drawing Records Table"
            />
          </section>
        </main>

        <aside
          className={styles.sidebarColumn}
          aria-label="Drawing collections"
        >
          <div className={styles.sectionHeaderRow}>
            <div>
              <h2 className={styles.sectionTitle}>Drawing collections</h2>
              <p className={styles.sectionSubtitle}>
                Browse material by its existing logical grouping.
              </p>
            </div>
          </div>

          <div className={styles.collectionsContainer}>
            <DrawingCollectionCard
              title="XREFs"
              subtext="Referenced drawing files"
              description="Cross-reference records grouped by floor and discipline."
              href="/more-info"
              icon="↗"
            />
            <DrawingCollectionCard
              title="Base build drawings"
              subtext="Core building drawing set"
              description="Base-build plans, schematics and coordinated records."
              href="/more-info"
              icon="▦"
            />
            <DrawingCollectionCard
              title="Post 2005 fitout drawings"
              subtext="Fitout drawing collections"
              description="Browse by discipline rather than through one long undifferentiated list."
              href="/more-info"
              icon="+"
              sublinks={[
                { label: "Mechanical & sprinkler", href: "/more-info" },
                { label: "Electrical & communication", href: "/more-info" },
                { label: "Control", href: "/more-info" },
                { label: "Building", href: "/more-info" },
              ]}
            />
            <DrawingCollectionCard
              title="Included archive records"
              subtext="3 files on this page"
              description="Small archive content is kept visible in the additional drawing records table."
              nonClickable={true}
              icon="▫"
            />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default withPage(Drawings)({
  meta: {
    title: "Drawing schedule | Meridian Tower | Pulse",
    description:
      "Floor plans, key schematics and linked drawing collections for Meridian Tower site.",
  },
  breadcrumbs: [
    { label: clientContent.systemName, href: "/" },
    { label: "Drawings" },
  ],
  searchPlaceholder: "Search drawings and documents",
});
